/**
 * DateStrip — the month-spined horizontal date strip.
 *
 * ONE component for both worlds (RULED 2026-08-19). It began as the client
 * booking flow's device-proven strip (CWR 2.4.1: a slim vertical month label
 * riding beside its month's chips, pinned while those chips scroll under it).
 * The admin composer had a CSS copy of it, which diverged and broke twice — so
 * the copy is gone and both sides mount this.
 *
 * It is PRESENTATIONAL: it owns the strip's shape and behaviour, never its
 * palette (see `DateStrip.module.css` — hosts map `--strip-*`) and never its
 * data. Each host builds `months` from its own source of truth and decides what
 * "selected" and "disabled" mean.
 */

import styles from './DateStrip.module.css';

export interface DateStripDay {
  /** `YYYY-MM-DD`. Identity, callback argument, and `day-cell-<date>` test id. */
  date: string;
  /** Short weekday, e.g. `MON` (rendered uppercase either way). */
  weekday: string;
  dayOfMonth: string | number;
  selected: boolean;
  /** Not choosable: renders dimmed and sets the button's `disabled`. */
  disabled?: boolean;
  /** Draws the availability dot under the number. */
  marked?: boolean;
  /** Accessible name. Falls back to the button's own text (weekday + number). */
  ariaLabel?: string;
}

export interface DateStripMonth {
  /** `yyyy-MM` — section identity. */
  key: string;
  /** Spine text, e.g. `June` or `Jun`. */
  label: string;
  days: DateStripDay[];
}

export interface DateStripProps {
  /** Month sections in display order; each host groups its own days. */
  months: DateStripMonth[];
  onSelect: (date: string) => void;
  /**
   * Names the strip as a group for assistive tech. Omitted ⇒ no `role`, for
   * hosts whose surrounding panel already labels the choice.
   */
  ariaLabel?: string;
  /** Host class carrying the `--strip-*` mapping (and any layout bleed). */
  className?: string;
  testId?: string;
}

export function DateStrip({ months, onSelect, ariaLabel, className, testId }: DateStripProps) {
  return (
    <div
      className={className ? `${styles.strip} ${className}` : styles.strip}
      data-testid={testId}
      {...(ariaLabel ? { role: 'group', 'aria-label': ariaLabel } : {})}
    >
      {months.map((month) => (
        <div className={styles.month} key={month.key}>
          <div className={styles.spine} aria-hidden="true">
            <span className={styles.spineLabel}>{month.label}</span>
          </div>
          {month.days.map((day) => (
            <button
              key={day.date}
              type="button"
              disabled={day.disabled ?? false}
              aria-pressed={day.selected}
              aria-label={day.ariaLabel}
              className={`${styles.chip} ${day.selected ? styles.selected : ''} ${
                day.disabled ? styles.disabled : ''
              }`}
              onClick={() => onSelect(day.date)}
              data-testid={`day-cell-${day.date}`}
            >
              <span className={styles.dow}>{day.weekday}</span>
              <span className={styles.dnum}>{day.dayOfMonth}</span>
              {day.marked && <span className={styles.dot} />}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
